import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type GuardFn = () => Promise<boolean>

interface NavGuardContextValue {
  /** Register an async guard. Pass null to clear. */
  register: (guard: GuardFn | null) => void
  /** Ask the registered guard (if any) whether navigation may proceed. */
  mayNavigate: () => Promise<boolean>
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const NavGuardContext = createContext<NavGuardContextValue | undefined>(undefined)

// ---------------------------------------------------------------------------
// Provider — router-independent (holds only a ref)
// ---------------------------------------------------------------------------

export function NavGuardProvider({ children }: { children: ReactNode }) {
  const guardRef = useRef<GuardFn | null>(null)

  const register = useCallback((guard: GuardFn | null) => {
    guardRef.current = guard
  }, [])

  const mayNavigate = useCallback(async (): Promise<boolean> => {
    if (!guardRef.current) return true
    return guardRef.current()
  }, [])

  return (
    <NavGuardContext.Provider value={{ register, mayNavigate }}>
      {children}
    </NavGuardContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook: useNavGuard — called by a route to register its "may I leave?" fn
// ---------------------------------------------------------------------------

/**
 * Register a guard function for the lifetime of the calling component.
 * Pass `null` to clear (or simply let the unmount cleanup handle it).
 * The guard is automatically unregistered on unmount.
 */
export function useNavGuard(guard: GuardFn | null): void {
  const ctx = useContext(NavGuardContext)
  if (!ctx) throw new Error('useNavGuard must be used within a NavGuardProvider')

  const { register } = ctx

  useEffect(() => {
    register(guard)
    return () => {
      // Unregister on unmount (guard may have been set to null already)
      register(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guard, register])
}

// ---------------------------------------------------------------------------
// Hook: useGuardedNavigate — wraps useNavigate with guard consultation
// ---------------------------------------------------------------------------

/**
 * Returns a navigate function that consults the registered guard before
 * navigating. If no guard is active, navigates immediately.
 */
export function useGuardedNavigate(): (to: string) => Promise<void> {
  const ctx = useContext(NavGuardContext)
  if (!ctx) throw new Error('useGuardedNavigate must be used within a NavGuardProvider')

  const navigate = useNavigate()
  const { mayNavigate } = ctx

  return useCallback(
    async (to: string) => {
      const allowed = await mayNavigate()
      if (allowed) navigate(to)
    },
    [mayNavigate, navigate],
  )
}
